/**
 * ChangePasswordPage.jsx  —  /change-password
 *
 * Gösterilme koşulu: login response'unda must_change_password: true
 * Şifre değişimi başarılıysa normal akışa devam edilir.
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, KeyRound, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import useReducedMotion from '../hooks/useReducedMotion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChangePasswordPage() {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const { authHeaders } = useAuth();
  const { lang } = useLanguage();
  const toast = useToast();
  const navigate = useNavigate();
  const reduced = useReducedMotion();

  useEffect(() => {
    document.body.classList.add('duo-page');
    return () => document.body.classList.remove('duo-page');
  }, []);

  const tr = (tr_, en) => (lang === 'tr' ? tr_ : en);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPw.length < 8) {
      const msg = tr(
        'Yeni şifre en az 8 karakter olmalıdır',
        'New password must be at least 8 characters'
      );
      setError(msg);
      return;
    }
    if (newPw !== confirmPw) {
      const msg = tr('Şifreler eşleşmiyor', 'Passwords do not match');
      setError(msg);
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/profile/change-password`,
        { current_password: currentPw, new_password: newPw },
        { headers: authHeaders() }
      );
      setDone(true);
      toast.success(tr('Şifreniz başarıyla değiştirildi', 'Password changed successfully'));
      setTimeout(() => navigate('/dashboard'), reduced ? 0 : 1200);
    } catch (err) {
      const msg = getApiErrorMessage(err, lang);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center duo-bg px-4 py-10">
      <div className="w-full max-w-md">
        {/* Logo */}
        <motion.div
          className="text-center mb-8"
          initial={reduced ? false : { opacity: 0, y: -10 }}
          animate={reduced ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link to="/" className="inline-flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'var(--duo-green)' }}
            >
              <span className="text-white font-duo-display text-2xl leading-none">K</span>
            </div>
            <span className="font-duo-display text-2xl text-[var(--duo-text)]">Kariyer Online</span>
          </Link>
        </motion.div>

        <motion.div
          className="bg-white rounded-2xl p-8"
          initial={reduced ? false : { opacity: 0, scale: 0.96 }}
          animate={reduced ? undefined : { opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          style={{ border: '2px solid var(--duo-stroke)' }}
        >
          {done ? (
            <div className="text-center" data-testid="change-pw-done">
              <motion.div
                className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center"
                style={{ backgroundColor: 'var(--duo-green)', boxShadow: '0 4px 0 0 var(--duo-green-dark)' }}
                initial={reduced ? false : { scale: 0.5, opacity: 0 }}
                animate={reduced ? undefined : { scale: [0.5, 1.2, 0.95, 1], opacity: 1 }}
                transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.5, 0.8, 1] }}
              >
                <CheckCircle2 size={32} strokeWidth={2.5} className="text-white" />
              </motion.div>
              <h2 className="font-duo-display text-2xl text-[var(--duo-text)] mb-2">
                {tr('Şifre Değiştirildi!', 'Password Changed!')}
              </h2>
              <p className="text-sm text-[var(--duo-text-soft)] font-medium">
                {tr('Yönlendiriliyorsunuz...', 'Redirecting...')}
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'var(--duo-yellow)', boxShadow: '0 3px 0 0 var(--duo-yellow-dark, #ca9a0d)' }}
                >
                  <KeyRound size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="font-duo-display text-xl text-[var(--duo-text)]">
                    {tr('Şifrenizi Değiştirin', 'Change Your Password')}
                  </h2>
                  <p className="text-xs text-[var(--duo-text-soft)] font-medium">
                    {tr(
                      'İlk girişinizde şifrenizi değiştirmeniz gerekmektedir.',
                      'You must change your password on your first login.'
                    )}
                  </p>
                </div>
              </div>

              {error && (
                <div
                  className="bg-[#FFF5F5] border-2 border-[var(--duo-error)] text-[var(--duo-error-dark)] px-4 py-2 rounded-xl mb-4 text-sm font-bold"
                  data-testid="change-pw-error"
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Current password */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[var(--duo-text-soft)] mb-2">
                    {tr('Mevcut Şifre', 'Current Password')}
                  </label>
                  <div className="input-duo-wrap">
                    <input
                      type={showCurrent ? 'text' : 'password'}
                      value={currentPw}
                      onChange={(e) => setCurrentPw(e.target.value)}
                      className="input-duo"
                      required
                      autoFocus
                      data-testid="current-pw-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrent(!showCurrent)}
                      className="input-duo-affix"
                      aria-label="Toggle visibility"
                    >
                      {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* New password */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[var(--duo-text-soft)] mb-2">
                    {tr('Yeni Şifre', 'New Password')}
                    <span className="ml-1 text-[var(--duo-text-light)] normal-case tracking-normal">
                      {tr('(min. 8 karakter)', '(min. 8 characters)')}
                    </span>
                  </label>
                  <div className="input-duo-wrap">
                    <input
                      type={showNew ? 'text' : 'password'}
                      value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      className="input-duo"
                      required
                      minLength={8}
                      data-testid="new-pw-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew(!showNew)}
                      className="input-duo-affix"
                      aria-label="Toggle visibility"
                    >
                      {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Confirm password */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[var(--duo-text-soft)] mb-2">
                    {tr('Yeni Şifre (Tekrar)', 'Confirm New Password')}
                  </label>
                  <div className="input-duo-wrap">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPw}
                      onChange={(e) => setConfirmPw(e.target.value)}
                      className={
                        'input-duo' + (confirmPw && confirmPw !== newPw ? ' has-error' : '')
                      }
                      required
                      data-testid="confirm-pw-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="input-duo-affix"
                      aria-label="Toggle visibility"
                    >
                      {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {confirmPw && confirmPw !== newPw && (
                    <p className="text-xs text-[var(--duo-error-dark)] mt-2 font-bold">
                      {tr('Şifreler eşleşmiyor', 'Passwords do not match')}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-duo-primary w-full mt-4"
                  data-testid="change-pw-submit"
                >
                  {loading
                    ? tr('Kaydediliyor...', 'Saving...')
                    : tr('Şifremi Değiştir', 'Change Password')}
                </button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
